import React, { Fragment } from 'react'
import { Form, Row, Col, DropdownButton, Dropdown, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap'

// components
import { ArrowRight } from 'react-feather'

// definitions
import { AccountXeroOrgWithData } from '../../../../screens/subsync/subsync'
import { StripeProduct, XeroAccount, XeroItem } from '../../../../../../back-end/utilities/apiDefinitions'
import { DetailedStripeOrgAccountXeroOrg, Error } from '../settings'

interface ProductMappingProps {
	stripeProducts: StripeProduct[]
	stripeOrgID: string
	orgMapping: DetailedStripeOrgAccountXeroOrg
	mappedXeroOrg: AccountXeroOrgWithData | undefined
	handleProductUpdate: (stripeOrgID: string, stripeProductID: string, name: string, value: string) => void
	handleProductAdd: (stripeOrgID: string, stripeProductID: string, name: string, value: string) => void
	handleApplyToAllProducts: (stripeOrgID: string, name: string, value: string) => void
	errors: Error[]
}

const ProductMapping = (props: ProductMappingProps) => {
	const [applyToAllSelected, setApplyToAllSelected] = React.useState<boolean>(false)
	const [searchFilter, setSearchFilter] = React.useState<string>('')
	const [onlyShowErrors, setOnlyShowErrors] = React.useState<boolean>(false)

	return (
		<>
			{props.stripeProducts.map((product, index) => {
				const productMapping = props.orgMapping.stripeProductXeroMappings!.find((mapping) => mapping.stripeProductID === product.stripeProductID)
				const selectedItem = productMapping
					? props.orgMapping.stripeOrgAccountXeroOrgMappingType === 'Account'
						? props.mappedXeroOrg!.accounts.find((account) => account.xeroAccountID === productMapping.xeroAccountID)
						: props.mappedXeroOrg!.items.find((item) => item.xeroItemID === productMapping.xeroItemID)
					: undefined
				const error = props.errors.find((error) => error.id === product.stripeID)
				return (
					<Fragment key={product.stripeProductID}>
						{index === 0 && (
							<>
								{props.errors.filter((error) => error.id).length > 0 && (
									<Row>
										<Col sm={2}>
											<Form.Label style={LabelStyle}>Only show rows with warnings</Form.Label>
										</Col>
										<Col>
											<Form.Check
												type={'switch'}
												id={`onlyShowErrors`}
												name={'onlyShowErrors'}
												checked={onlyShowErrors}
												onChange={(e) => setOnlyShowErrors(e.target.checked)}
											/>
										</Col>
									</Row>
								)}
								<Row>
									<Col sm={2}>
										<Form.Label style={LabelStyle}>Apply to All Products</Form.Label>
									</Col>
									<Col>
										<Form.Check
											type={'switch'}
											id={`applyToAllProducts${applyToAllSelected ? '-enabled' : ''}`}
											name={'stripeFeesBillsEnabled'}
											checked={applyToAllSelected}
											onChange={(e) => {
												setApplyToAllSelected(e.target.checked)
												if (e.target.checked && (selectedItem || productMapping?.ignoreInvoices)) {
													props.handleApplyToAllProducts(
														props.stripeOrgID,
														`xero${props.orgMapping.stripeOrgAccountXeroOrgMappingType}ID`,
														productMapping?.ignoreInvoices
															? 'ignoreInvoices'
															: props.orgMapping.stripeOrgAccountXeroOrgMappingType === 'Account'
															? (selectedItem as XeroAccount).xeroAccountID!
															: (selectedItem as XeroItem).xeroItemID!
													)
												}
											}}
										/>
									</Col>
								</Row>
							</>
						)}
						<div className={(error ? 'error ' : '') + (onlyShowErrors && !error ? 'd-none ' : '') + 'subsync-product-row'}>
							<Row>
								<Col
									style={{
										display: 'flex',
										flexDirection: 'column',
										justifyItems: 'center',
									}}
								>
									{product.stripeID?.startsWith('prod_') ? (
										<OverlayTrigger
											placement={'left'}
											overlay={
												<Tooltip id={`${product.stripeProductID}-tooltip`}>
													{product.stripeID}
													{product.description ? `: ${product.description}` : ''}
												</Tooltip>
											}
										>
											<div className="subsync-settings-label-small">
												{product.name}
												{/* isActive can be null for products created before column was added and not yet updated */}
												{product.isActive === false ? ' [Archived]' : ''}
											</div>
										</OverlayTrigger>
									) : (
										<div className="subsync-settings-label-small">{product.name}</div>
									)}
									{error ? <small className="error-message">{error.message}</small> : null}
								</Col>
								<Col
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
									}}
								>
									<ArrowRight size="16"></ArrowRight>
								</Col>
								<Col
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'flex-end',
									}}
								>
									<DropdownButton
										id={'mapping-account-select'}
										title={
											productMapping?.[`xero${props.orgMapping.stripeOrgAccountXeroOrgMappingType as 'Account' | 'Item'}ID`] === 'N/A'
												? 'Not in use'
												: productMapping?.ignoreInvoices
												? 'Ignore'
												: selectedItem
												? selectedItem.name
												: `Select Xero ${props.orgMapping.stripeOrgAccountXeroOrgMappingType}`
										}
										onSelect={(eventKey) => {
											if (productMapping) {
												props.handleProductUpdate(
													props.stripeOrgID,
													product.stripeProductID!,
													`xero${props.orgMapping.stripeOrgAccountXeroOrgMappingType}ID`,
													eventKey!
												)
											} else {
												props.handleProductAdd(
													props.stripeOrgID,
													product.stripeProductID!,
													`xero${props.orgMapping.stripeOrgAccountXeroOrgMappingType}ID`,
													eventKey!
												)
											}
											if (applyToAllSelected) {
												props.handleApplyToAllProducts(
													props.stripeOrgID,
													`xero${props.orgMapping.stripeOrgAccountXeroOrgMappingType}ID`,
													eventKey!
												)
											}
										}}
										disabled={applyToAllSelected && index > 0}
									>
										<Dropdown.Item as="div">
											<Form.Control
												type="text"
												placeholder="Search or enter value"
												value={searchFilter}
												onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()} // Prevent dropdown from closing on input click
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchFilter(e.target.value)} // Handle input changes
											/>
										</Dropdown.Item>
										{props.orgMapping.stripeOrgAccountXeroOrgMappingType === 'Account' ? (
											<>
												<Dropdown.Header>Revenue</Dropdown.Header>
												{props
													.mappedXeroOrg!.accounts.filter(
														(account) =>
															account.type === 'REVENUE' && account.name?.toLowerCase().includes(searchFilter.toLowerCase())
													)
													.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
													.map((xa) => (
														<Dropdown.Item key={xa.xeroAccountID} eventKey={xa.xeroAccountID}>
															{xa.name}
														</Dropdown.Item>
													))}
												<Dropdown.Header>Other Income</Dropdown.Header>
												{props
													.mappedXeroOrg!.accounts.filter(
														(account) =>
															account.type === 'OTHERINCOME' && account.name?.toLowerCase().includes(searchFilter.toLowerCase())
													)
													.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
													.map((xa) => (
														<Dropdown.Item key={xa.xeroAccountID} eventKey={xa.xeroAccountID}>
															{xa.name}
														</Dropdown.Item>
													))}
												<Dropdown.Header>Sales</Dropdown.Header>
												{props
													.mappedXeroOrg!.accounts.filter(
														(account) =>
															account.type === 'SALES' && account.name?.toLowerCase().includes(searchFilter.toLowerCase())
													)
													.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
													.map((xa) => (
														<Dropdown.Item key={xa.xeroAccountID} eventKey={xa.xeroAccountID}>
															{xa.name}
														</Dropdown.Item>
													))}
												<Dropdown.Header>Current Liability</Dropdown.Header>
												{props
													.mappedXeroOrg!.accounts.filter(
														(account) =>
															account.type === 'CURRLIAB' && account.name?.toLowerCase().includes(searchFilter.toLowerCase())
													)
													.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
													.map((xa) => (
														<Dropdown.Item key={xa.xeroAccountID} eventKey={xa.xeroAccountID}>
															{xa.name}
														</Dropdown.Item>
													))}
											</>
										) : (
											props
												.mappedXeroOrg!.items.filter(
													(item) => item.name && item.name.toLowerCase().includes(searchFilter.toLowerCase())
												)
												.sort((a, b) => a.code!.localeCompare(b.code!, undefined, { numeric: true }))
												.map((xi) => (
													<Dropdown.Item key={xi.xeroItemID} eventKey={xi.xeroItemID}>
														<Badge variant="light" className="p-1 mr-1">
															{xi.code}
														</Badge>
														{xi.name}
													</Dropdown.Item>
												))
										)}
										<Dropdown.Divider />
										<Dropdown.Item eventKey="ignoreInvoices">Ignore</Dropdown.Item>
										{product.isActive === false && <Dropdown.Item eventKey="N/A">Not in use</Dropdown.Item>}
									</DropdownButton>
								</Col>
							</Row>
							{productMapping?.ignoreInvoices && (
								<Row className="justify-content-end pt-2">
									<Col sm="auto" className="font-italic">
										Any invoice containing this product will be ignored by SubSync
									</Col>
								</Row>
							)}
						</div>
					</Fragment>
				)
			})}
		</>
	)
}

const LabelStyle = {
	lineHeight: '2.5rem',
}

export { ProductMapping }
export type { ProductMappingProps }
